"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useTranslations } from "next-intl"
import { LocaleSuggestionBanner } from "@/components/locale-suggestion-banner"
import { SiteHeader } from "@/components/site-header"
import { SiteFooter } from "@/components/site-footer"
import { motion } from "framer-motion"

// Animation variants
const easeOut = [0.16, 1, 0.3, 1] as const

const fadeInUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easeOut } }
}

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
}

export default function ImpressumPage() {
  const t = useTranslations()
  const tImpressum = useTranslations("impressum")

  // Navigation links
  const navigationLinks = [
    { name: t("nav.features"), href: "/#features" },
    { name: t("nav.pricing"), href: "/#pricing" },
    { name: t("nav.about"), href: "/about" },
    { name: t("nav.contact"), href: "/impressum" },
  ]

  // Footer links
  const footerLinks = {
    product: [
      { name: t("footer.links.features"), href: "/#features" },
      { name: t("footer.links.pricing"), href: "/#pricing" },
    ],
    company: [
      { name: t("footer.links.about"), href: "/about" },
      { name: t("footer.links.contact"), href: "/impressum" },
    ],
    legal: [
      { name: t("footer.links.privacy"), href: "/privacy" },
      { name: t("footer.links.terms"), href: "/terms" },
      { name: t("footer.links.gtc"), href: "/gtc" },
      { name: t("footer.links.betaTerms"), href: "/beta-terms" },
    ],
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-100 focus:px-4 focus:py-2 focus:bg-accent focus:text-accent-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
      >
        {t('common.skipToContent')}
      </a>

      {/* Locale Suggestion Banner */}
      <LocaleSuggestionBanner />

      {/* Header */}
      <SiteHeader navigationLinks={navigationLinks} />

      <main id="main-content">
        {/* Hero Section */}
        <section className="py-28 md:py-44 relative overflow-hidden" aria-labelledby="hero-heading">
          <div className="max-w-6xl px-4 md:px-6 mx-auto">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={stagger}
              className="text-center space-y-8"
            >
              <motion.div variants={fadeInUp}>
                <h1 id="hero-heading" className="text-[2.75rem] md:text-6xl lg:text-[5.25rem] font-normal tracking-tight leading-[1.2]">
                  {tImpressum("title")}
                </h1>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Impressum Content Section */}
        <section className="py-28 md:py-44 bg-background" aria-labelledby="impressum-heading">
          <div className="max-w-4xl px-4 md:px-6 mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15, margin: "-60px" }}
              variants={stagger}
              className="space-y-8"
            >
              <motion.div variants={fadeInUp}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl font-semibold">{tImpressum("responsible.title")}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="font-semibold text-foreground mb-2">{tImpressum("responsible.name")}</p>
                      <p className="text-muted-foreground">{tImpressum("responsible.address")}</p>
                      <p className="text-muted-foreground">{tImpressum("responsible.city")}</p>
                    </div>
                    <div className="pt-4 border-t">
                      <p className="font-semibold text-foreground mb-2">{tImpressum("contact.title")}</p>
                      <p className="text-muted-foreground">
                        {tImpressum("contact.email")}: <a href={`mailto:${tImpressum("contact.emailValue")}`} className="text-accent hover:text-accent/90 underline">{tImpressum("contact.emailValue")}</a>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* <motion.div variants={fadeInUp}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl font-semibold">{tImpressum("legal.title")}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="font-semibold text-foreground mb-2">{tImpressum("legal.businessRegistration.title")}</p>
                      <p className="text-muted-foreground">{tImpressum("legal.businessRegistration.content")}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-2">{tImpressum("legal.taxId.title")}</p>
                      <p className="text-muted-foreground">{tImpressum("legal.taxId.content")}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-2">{tImpressum("legal.chamber.title")}</p>
                      <p className="text-muted-foreground">{tImpressum("legal.chamber.content")}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div> */}

              <motion.div variants={fadeInUp}>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl font-semibold">{tImpressum("dispute.title")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">{tImpressum("dispute.content")}</p>
                  </CardContent>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <SiteFooter footerLinks={footerLinks} />
    </div>
  )
}
